import { useState, useCallback } from 'react';
import {
  getProjectContentSettings,
  saveProjectContentSettings,
  type ProjectContentSettings,
} from '@/lib/project-content-settings-storage';

export interface UseProjectContentSettingsReturn {
  settings: ProjectContentSettings;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  updateSetting: (key: keyof Omit<ProjectContentSettings, 'lastUpdated'>, value: boolean) => void;
  saveSettings: () => Promise<void>;
  resetToDefaults: () => void;
}

export function useProjectContentSettings(): UseProjectContentSettingsReturn {
  const [settings, setSettings] = useState<ProjectContentSettings>(getProjectContentSettings);
  const [savedSettings, setSavedSettings] = useState<ProjectContentSettings>(getProjectContentSettings);
  const [isSaving, setIsSaving] = useState(false);

  const hasUnsavedChanges =
    settings.showObjectives !== savedSettings.showObjectives ||
    settings.showKeyDeliverables !== savedSettings.showKeyDeliverables ||
    settings.showNotes !== savedSettings.showNotes;

  const updateSetting = useCallback(
    (key: keyof Omit<ProjectContentSettings, 'lastUpdated'>, value: boolean) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveSettings = useCallback(async () => {
    setIsSaving(true);
    try {
      // Phase 1: localStorage
      // TODO [ENGINEERING — Devin]: Replace with PATCH /api/company/project-content-settings
      await new Promise((resolve) => setTimeout(resolve, 400));
      saveProjectContentSettings(settings);
      setSavedSettings(settings);
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  const resetToDefaults = useCallback(() => {
    setSettings(getProjectContentSettings());
  }, []);

  return {
    settings,
    hasUnsavedChanges,
    isSaving,
    updateSetting,
    saveSettings,
    resetToDefaults,
  };
}
